import { Component, type ReactNode } from 'react';

const RELOAD_KEY = 'auditplus-chunk-reload';

/** A failed dynamic import (a tab from before a deploy asks for a chunk hash that is gone, or no network). */
function isChunkLoadError(err: unknown): boolean {
  const m = err instanceof Error ? err.message : String(err);
  return /dynamically imported module|Importing a module script failed|Failed to fetch|Load failed|error loading dynamically imported/i.test(m);
}

/**
 * Around the lazy routes (App.tsx): a page whose code failed to load must
 * not blank the whole app. A chunk that vanished after a deploy is fixed by
 * one reload (the new index.html names the new hashes) — done once, so a
 * real outage can't loop; otherwise, or when offline, a message with a
 * Reload button. sessionStorage holds only the one-shot marker (never audit
 * data).
 */
export class LazyBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(err: unknown): void {
    if (!isChunkLoadError(err) || !navigator.onLine) return;
    try {
      const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
      if (Date.now() - last < 60_000) return; // reloaded for this already — don't loop
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    } catch {
      // Storage blocked: just show the message below.
      return;
    }
    window.location.reload();
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="card" style={{ margin: '1rem' }}>
        <p>This page couldn’t load{navigator.onLine ? ' — the app may have just been updated' : ' — you seem to be offline'}.</p>
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}
